import { ActionIcon, Flex, Text } from '@mantine/core';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge.tsx';
import { createPrismaDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { TableRowActionsMenu } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { MipClassifierRes } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

type Handlers = {
	onEdit: (classifier: MipClassifierRes) => void;
	onArchiveToggle: (classifier: MipClassifierRes) => void;
	onManageTags: (classifier: MipClassifierRes) => void;
	onRemoveTag: (cohortId: string, tagId: string) => void;
};

type GetClassifiersTableColumnsParams = {
	handlers: Handlers;
	canEdit: boolean;
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	tagsByClassifierId: Record<string, { id: string; name: string }[]>;
};

export const getClassifiersTableColumns = ({
	handlers,
	canEdit,
	tMipManage,
	tagsByClassifierId,
}: GetClassifiersTableColumnsParams): ExtendedColumnDef<MipClassifierRes>[] => {
	const { onEdit, onArchiveToggle, onManageTags, onRemoveTag } = handlers;

	const columns: ExtendedColumnDef<MipClassifierRes>[] = [
		createTextColumn({
			id: 'name',
			headerText: tMipManage('root.classifiers.column.name'),
			columnLabel: tMipManage('root.classifiers.column.name'),
			accessorKey: 'name',
			showFilter: false,
			filterVariant: 'text',
		}),
		createTextColumn({
			id: 'tags',
			headerText: tMipManage('root.classifiers.column.tags'),
			columnLabel: tMipManage('root.classifiers.column.tags'),
			accessorFn: (row) => (tagsByClassifierId[row.id] ?? []).map((t) => t.name).join(', '),
			showFilter: false,
			cell: ({ row }) => {
				const tags = tagsByClassifierId[row.original.id] ?? [];
				if (!tags.length) {
					return <Text c="dimmed">—</Text>;
				}
				return (
					<Flex w="100%" gap="0.25rem" wrap="wrap" py="0.25rem">
						{tags.map((tag) => (
							<StatusBadge
								text={tag.name}
								color="gray.4"
								textColor="dark.8"
								key={tag.id}
								rightSection={
									canEdit && (
										<ActionIcon
											size="xs"
											variant="transparent"
											c="dark.8"
											p="0"
											mih="unset"
											miw="unset"
											h="unset"
											w="unset"
											onClick={() => onRemoveTag(row.original.id, tag.id)}
										>
											<Icon variant="close-v3" size="sm" />
										</ActionIcon>
									)
								}
							/>
						))}
					</Flex>
				);
			},
		}),
		createTextColumn({
			id: 'archived',
			headerText: tMipManage('root.classifiers.column.status'),
			columnLabel: tMipManage('root.classifiers.column.status'),
			accessorFn: (row) =>
				row.archived
					? tMipManage('root.classifiers.status.archived')
					: tMipManage('root.classifiers.status.active'),
			showFilter: false,
			cell: ({ row }) =>
				row.original.archived ? (
					<StatusBadge
						text={tMipManage('root.classifiers.status.archived')}
						color="gray.4"
						textColor="dark.8"
					/>
				) : (
					<StatusBadge
						text={tMipManage('root.classifiers.status.active')}
						color="teal.8"
					/>
				),
		}),
		createPrismaDateColumn({
			id: 'createdAt',
			headerText: tMipManage('root.classifiers.column.created_at'),
			columnLabel: tMipManage('root.classifiers.column.created_at'),
			accessorKey: 'createdAt',
			enableSorting: true,
			enableColumnFilter: false,
		}),
	];

	if (canEdit) {
		columns.push({
			id: 'actions',
			header: '',
			enableSorting: false,
			enableColumnFilter: false,
			size: 56,
			cell: ({ row }) => (
				<TableRowActionsMenu
					options={[
						{
							label: tMipManage('root.classifiers.action.edit'),
							onClick: () => onEdit(row.original),
						},
						{
							label: tMipManage('root.classifiers.action.manage_tags'),
							onClick: () => onManageTags(row.original),
						},
						{
							label: row.original.archived
								? tMipManage('root.classifiers.action.unarchive')
								: tMipManage('root.classifiers.action.archive'),
							onClick: () => onArchiveToggle(row.original),
						},
					]}
				/>
			),
		});
	}

	return columns;
};
